<template>
  <component
    :is="valueInputComponent"
    v-model="modelValue"
    :data="valueInputData"
    :container
    :size
    :readonly
    :placeholder="t('Select value')"
    v-bind="valueInputAttrs"
    @ready="handleReady"
  >
    <template
      v-if="loading"
      #trailing
    >
      <UIPreloader />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue'
import { isEqual } from 'lodash'
import { useDebounceFn } from '@vueuse/core'

import { ButtonSize, DropdownListData, I18nTranslate } from '@types'
import { FilterComparison } from '../utils/enums'
import { Filter, FilterField } from '../utils/types'

import { FILTER_LAST_PERIODS } from './utils/const'

import { monthDateToName } from '@/helpers/dates'

import { UIInputAutocomplete, UIInputSelect } from '@ui/fields'
import { UIPreloader } from '@ui/core'

type Props = {
  fieldSettings?: FilterField
  comparison?: FilterComparison
  readonly?: boolean
  filterContext?: Filter
}

const props = defineProps<Props>()

const modelValue = defineModel<any>()

defineExpose({
  start,
  init() {
    initData()
  },
})

const container = inject<string | undefined>('container', undefined)
const size = inject<ButtonSize>('size')
const t = inject<I18nTranslate>('t', (data: string) => data)

const inputRef = ref<HTMLInputElement>()
const internalListData = ref<DropdownListData[]>()

const loading = ref(false)

const isSelectField = computed(
  () =>
    props.comparison === FilterComparison.LAST || props.fieldSettings?.isMonth,
)

const valueInputComponent = computed(() =>
  isSelectField.value ? UIInputSelect : UIInputAutocomplete,
)

const dateData = computed(
  () =>
    (props.fieldSettings?.isMonth
      ? listData.value?.map(item => ({
        ...item,
        label: monthDateToName(`1999-${item.value}-01`),
      }))
      : FILTER_LAST_PERIODS.map(value => ({
        value: value,
        label: `${value} ${t('days')}`,
      }))) || [],
)

const listData = computed(() => {
  const base = props.fieldSettings?.list || internalListData.value || []
  // ensure current value stays in options
  const current = modelValue.value
  if (
    !isSelectField.value &&
    (typeof current === 'string' || typeof current === 'number') &&
    `${current}` !== '' &&
    !base.some(item => `${item.value}` === `${current}`)
  ) {
    return [...base, { value: `${current}`, label: `${current}` }]
  }
  return base
})

const valueInputData = computed(() =>
  isSelectField.value ? dateData.value : listData.value,
)

const valueInputAttrs = computed(() =>
  isSelectField.value ? {} : { placement: 'bottom-end' },
)

let initDataGeneration = 0

const initData = useDebounceFn(async () => {
  const generation = ++initDataGeneration
  internalListData.value = undefined
  const getter = props.fieldSettings?.getter
  if (!getter) return
  loading.value = true
  try {
    // comparison can change options
    internalListData.value = await getter(props.filterContext, props.comparison)
  } finally {
    if (generation === initDataGeneration) {
      loading.value = false
    }
  }
}, 200)

async function start() {
  await initData()
  inputRef.value?.focus()
}

const handleReady = (el: HTMLInputElement) => {
  inputRef.value = el
}

onBeforeMount(initData)

// re-narrow options when other active params or comparison change while mounted
watch(
  () => [props.filterContext, props.comparison] as const,
  ([value, comparison], [prevValue, prevComparison]) => {
    if (isEqual(value, prevValue) && comparison === prevComparison) return
    initData()
  },
  { deep: true },
)
</script>
